// Hooks específicos para Events
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { EventService } from '../services/eventService';
import { toastSuccess, toastError } from '@/lib/toast-utils';
import type { EventFilters, EventCreateRequest } from '../models/event.types';
import type { Event } from '@/types/schema';

// Query keys para cache
export const eventQueryKeys = {
    all: ['events'] as const,
    lists: () => [...eventQueryKeys.all, 'list'] as const,
    list: (filters: EventFilters) => [...eventQueryKeys.lists(), { filters }] as const,
    details: () => [...eventQueryKeys.all, 'detail'] as const,
    detail: (id: string) => [...eventQueryKeys.details(), id] as const,
};

// Hook para buscar eventos com filtros
export function useEvents(filters: EventFilters = {}) {
    return useQuery({
        queryKey: eventQueryKeys.list(filters),
        queryFn: () => EventService.getAll(filters),
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

// Hook para buscar evento por ID
export function useEvent(id: string) {
    return useQuery({
        queryKey: eventQueryKeys.detail(id),
        queryFn: () => EventService.getById(id),
        enabled: !!id,
        staleTime: 5 * 60 * 1000, // 5 minutos
    });
}

// Hook para criar evento
export function useCreateEvent() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (eventData: EventCreateRequest) => EventService.create(eventData),
        onSuccess: (newEvent) => {
            // Invalidar cache dos eventos
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.all });

            toastSuccess({
                title: "Evento criado!",
                description: `O evento "${newEvent.title}" foi criado com sucesso.`,
            });
        },
        onError: (error: Error) => {
            toastError({
                title: "Erro ao criar evento",
                description: error.message,
            });
        },
    });
}

// Hook para atualizar evento
export function useUpdateEvent() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, data }: { id: string; data: Partial<EventCreateRequest> }) =>
            EventService.update(id, data),
        onSuccess: (updatedEvent) => {
            // Invalidar cache dos eventos
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.all });
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.detail(updatedEvent.id) });

            toastSuccess({
                title: "Evento atualizado!",
                description: `O evento "${updatedEvent.title}" foi atualizado com sucesso.`,
            });
        },
        onError: (error: Error) => {
            toastError({
                title: "Erro ao atualizar evento",
                description: error.message,
            });
        },
    });
}

// Hook para deletar evento
export function useDeleteEvent() {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => EventService.delete(id),
        onSuccess: () => {
            // Invalidar cache dos eventos
            queryClient.invalidateQueries({ queryKey: eventQueryKeys.all });

            toastSuccess({
                title: "Evento deletado!",
                description: "O evento foi removido com sucesso.",
            });
        },
        onError: (error: Error) => {
            toastError({
                title: "Erro ao deletar evento",
                description: error.message,
            });
        },
    });
}

